import type { ShapeType } from '../types/shape';
import type { RenderNode } from '../types/commontypes';
import { globalShapeStore } from '../stores/globalShapeStore';
import { defaultShapePropertyValues } from './defaultShapePropertyValues';

/**
 * Non-hook helper: returns a transient template RenderNode for the given shape type
 * composed from `lastShapeProperties[type] ?? defaultShapePropertyValues[type]`
 * plus the `type` discriminator. Used by class-component wrappers in their render
 * method (e.g. for tempLine preview) where hooks can't be called.
 *
 * Result is **memoized by `lastShapeProperties` reference**: zustand creates a new
 * top-level reference whenever any property changes (`updateLastShapeProperty`),
 * so the same return object is reused across renders until user edits a shape.
 * This keeps the WeakMap caches inside `toXxxRFCProps` adapters effective for
 * the drawing preview path (`each.appearance` was previously rebuilt every render).
 */
const templateCache = new WeakMap<object, Map<ShapeType, Partial<RenderNode>>>();

export function getActiveTemplateNode<T extends ShapeType>(type: T): Partial<RenderNode> {
  const allLastProps = globalShapeStore.getState().lastShapeProperties;
  let perStore = templateCache.get(allLastProps);
  if (!perStore) {
    perStore = new Map();
    templateCache.set(allLastProps, perStore);
  }
  const existing = perStore.get(type);
  if (existing) return existing;

  const props = allLastProps[type] ?? defaultShapePropertyValues[type] ?? {};
  const created: Partial<RenderNode> = { ...props, type };
  perStore.set(type, created);
  return created;
}
